import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { Button, Form, Message, Segment } from 'semantic-ui-react';

import HashtagsInput from '../../components/hashtags-input';
import NegativeKeywordsInput from '../../components/negative-keywords-input';
import InstagramLocationsInput from '../../components/instagram-locations-input';
import { transformHashtagstIntoArray } from '../../utils/hashtags-utils';

const toMoment = value => (value ? moment(value) : null);

// Name, dates, hashtags, negative keywords and locations only — budget and currency are not used by JUMBO campaigns.
class SettingsTab extends Component {
  constructor(props) {
    super(props);
    this.state = this.stateFromCampaign(props.campaign);
  }

  componentWillReceiveProps(nextProps) {
    if (_.get(nextProps, 'campaign.id') !== _.get(this.props, 'campaign.id')) {
      this.setState(this.stateFromCampaign(nextProps.campaign));
    }
  }

  onInputChange = (event, { name, value }) => {
    this.setState({ [name]: value, hasChanged: true, savedOk: false, hasFailed: false });
  };

  onLiveDateChange = liveDate => {
    this.setState({ liveDate, hasChanged: true, savedOk: false, hasFailed: false });
  };

  onEndDateChange = endDate => {
    this.setState({ endDate, hasChanged: true, savedOk: false, hasFailed: false });
  };

  onNegativeKeywordsChange = negativeKeywords => {
    this.setState({ negativeKeywords, hasChanged: true, savedOk: false, hasFailed: false });
  };

  onLocationsChange = locations => {
    this.setState({ locations, hasChanged: true, savedOk: false, hasFailed: false });
  };

  onSubmit = () => {
    const { name, liveDate, endDate, hashtags, negativeKeywords, locations } = this.state;
    this.setState({ isSaving: true, hasFailed: false });

    this.props.onSave({
      name: _.trim(name),
      hashtags,
      negativeKeywords,
      locations,
      liveDate: liveDate && liveDate.isValid() ? liveDate.format() : null,
      endDate: endDate && endDate.isValid() ? endDate.format() : null,
    })
      .then(() => this.setState({ isSaving: false, hasChanged: false, savedOk: true }))
      .catch(() => this.setState({ isSaving: false, hasFailed: true }));
  };

  stateFromCampaign = (campaign = {}) => ({
    name: campaign.name || '',
    hashtags: campaign.hashtags || '',
    negativeKeywords: campaign.negativeKeywords || '',
    locations: _.isArray(campaign.locations) ? campaign.locations : [],
    liveDate: toMoment(campaign.liveDate),
    endDate: toMoment(campaign.endDate),
    hasChanged: false,
    isSaving: false,
    savedOk: false,
    hasFailed: false,
  });

  isValid = () => {
    const { name, liveDate, endDate } = this.state;
    if (!_.trim(name)) return false;
    if (liveDate && endDate && endDate.isBefore(liveDate)) return false;
    return true;
  };

  render() {
    const {
      name, hashtags, negativeKeywords, locations, liveDate, endDate, hasChanged, isSaving, savedOk, hasFailed,
    } = this.state;
    const datesInverted = liveDate && endDate && endDate.isBefore(liveDate);

    return (
      <Segment.Group>
        <Segment secondary><strong>Campaign settings</strong></Segment>
        <Segment>
          <Form style={{ maxWidth: 620 }}>
            <Form.Field required>
              <label htmlFor="jumbo-campaign-name">Campaign name</label>
              <Form.Input
                id="jumbo-campaign-name"
                name="name"
                value={name}
                onChange={this.onInputChange}
                placeholder="e.g. Sky Westworld"
              />
            </Form.Field>

            <Form.Group widths="equal">
              <Form.Field>
                <label htmlFor="jumbo-live-date">Start date</label>
                <DatePicker
                  onChange={this.onLiveDateChange}
                  placeholderText="e.g. 01/05/2026"
                  selected={liveDate && liveDate.isValid() ? liveDate : null}
                  dateFormat="MMM Do YYYY"
                />
              </Form.Field>
              <Form.Field>
                <label htmlFor="jumbo-end-date">End date</label>
                <DatePicker
                  onChange={this.onEndDateChange}
                  placeholderText="e.g. 20/05/2026"
                  selected={endDate && endDate.isValid() ? endDate : null}
                  dateFormat="MMM Do YYYY"
                  popperPlacement="top-start"
                />
              </Form.Field>
            </Form.Group>

            {datesInverted &&
              <Message negative size="small" content="End date is before the start date." />}

            <HashtagsInput
              hashtags={transformHashtagstIntoArray(hashtags)}
              onValueChanged={this.onInputChange}
            />

            <NegativeKeywordsInput keywords={negativeKeywords} onChange={this.onNegativeKeywordsChange} />

            <InstagramLocationsInput
              locations={locations}
              onChange={this.onLocationsChange}
              onLookup={this.props.onLocationLookup}
              onSearch={this.props.onLocationSearch}
            />
          </Form>
        </Segment>
        <Segment secondary>
          <Button
            primary
            icon="save"
            labelPosition="left"
            content={isSaving ? 'Saving…' : 'Save'}
            loading={isSaving}
            disabled={!hasChanged || isSaving || !this.isValid()}
            onClick={this.onSubmit}
          />
          {savedOk && <span style={{ marginLeft: 12, color: '#21ba45' }}>Saved.</span>}
          {hasFailed && <span style={{ marginLeft: 12, color: '#db2828' }}>Could not save. Please try again.</span>}
        </Segment>
      </Segment.Group>
    );
  }
}

SettingsTab.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    hashtags: PropTypes.string,
    negativeKeywords: PropTypes.string,
    locations: PropTypes.arrayOf(PropTypes.object),
    liveDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onLocationLookup: PropTypes.func.isRequired,
  onLocationSearch: PropTypes.func.isRequired,
};

export default SettingsTab;



/** WEBPACK FOOTER **
 ** ./src/containers/large-campaign/settings-tab.js
 **/